import React, { useState, useRef, useEffect, useMemo } from "react";
import moment from "moment/moment";
import { commentDislikeToggle, commentLikeToggle, questionDislikeToggle, questionLikeToggle } from "../../../utils/api";
import { MESSAGE_TYPE, SORT_QUERY } from "../../../constants/message-types";
import { encodeHtmlChars, replaceEmojiToImage, replaceUrlsToLinks } from "../../../helpers/html-helpers";
import getFullname from "../../../helpers/get-fullname";
import storage from "../../../../../player/js/storage";
import { useLocaleContext } from "../../../context/LocaleContext";
import { useEventContext } from "../../../context/EventContext";
import { getUserNickname, UserAvatar } from "../../User";
import { LikeMessage, PinMessage, Checkmark, More } from "../../Icons/IconPackage/Message";
import WriteCommentForm from "../../CommentMessages/WriteCommentForm";

import "./style.scss";

const showRoles = ["moderator", "organizer", "speaker", "technical_support"];

const getUserRating = (usersArr, userId) => {
    let value = null;
    if (Array.isArray(usersArr)) {
        if (usersArr.includes(userId)) {
            value = 'like';
        }
        const targetItem = usersArr.find((item) => item.userId === userId);
        if (targetItem) {
            value = targetItem.value
        }
    }

    return value;
};

const ratingFuncs = {
    [MESSAGE_TYPE.COMMENT]: {
        like: commentLikeToggle,
        dislike: commentDislikeToggle,
    },
    [MESSAGE_TYPE.QUESTION]: {
        like: questionLikeToggle,
        dislike: questionDislikeToggle,
    },
};

const Message = (
    {
        message,
        onWriteAnswer,
        likesEnabled,
        dislikesEnabled,
        allowAnonymousMessage = false,
        currentUser,
        type = MESSAGE_TYPE.COMMENT,
        fixedLine,
        changeOpenMessageIndex,
        isHideMessage,
        checkAuth,
        stream,
        isHideTime,
        episodes,
        moduleRef,
        isHybrid,
        anonymous,
        setAnonymous,
        avatarsDisabled,
        keyboardShowHandler,
        isLastChild,
    }) => {
    const $locale = useLocaleContext();
    const messageTextRef = useRef(null);
    const moreDataRef = useRef(null);
    const dataRef = useRef(null);

    const { event, eventKey } = useEventContext();
    const { moduleQuestion, chatMessageThemes } = event || {};

    const ratedStorageKey = `${eventKey}/messages/${type}/rated`;
    const ratedMessages = storage.get(ratedStorageKey) || {};

    const messageClient = Object.hasOwn(message, 'clientRegistration')
        ? message?.clientRegistration
        : message?.client;
    const currentUserId = (currentUser?.clientRegistrationId ?? currentUser?.id) || null;
    const clientRegistrationRole = Object.hasOwn(message, 'clientRegistration')
        ? message?.clientRegistration.role
        : message?.clientRegistrationRole;

    const [isAnswerFormVisible, setAnswerFormVisible] = useState(false);
    const [isCanOpen, setIsCanOpen] = useState(false);
    const [isOpenMessage, setIsOpenMessage] = useState(false);
    const [isShowMoreData, setIsShowMoreData] = useState(false);
    const [userRating, setUserRating] = useState(() => getUserRating(ratedMessages[message.id], currentUserId));
    const [ratingUpdating, setRatingUpdating] = useState(false);

    const className = 'comment-message';
    const isOutgoing = currentUserId && messageClient?.id === currentUserId;
    // const isOutgoing = (messageClient?.id ?? messageClient.id) === currentUserId;
    const role = showRoles.includes(clientRegistrationRole) && clientRegistrationRole;
    const isRatingEnabled = moduleQuestion?.sortMethodType === SORT_QUERY.SORT_RATING && type === MESSAGE_TYPE.QUESTION;

    useEffect(() => {
        if (
            fixedLine &&
            messageTextRef.current?.offsetHeight < messageTextRef.current?.scrollHeight
        ) {
            setIsCanOpen(true);
        }
    }, []);

    useEffect(() => {
        if (!isShowMoreData) {
            return;
        }

        const handleClickOutsideMoreData = (event) => {
            const isTouchData = dataRef.current && dataRef.current.contains(event.target);
            const isTouchMoreData = moreDataRef.current && moreDataRef.current.contains(event.target);
            if (!isTouchData && !isTouchMoreData) {
                setIsShowMoreData(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutsideMoreData);

        return () => {
            document.removeEventListener('mousedown', handleClickOutsideMoreData);
        }
    }, [isShowMoreData]);

    useEffect(() => {
        if (isHideMessage) {
            setIsOpenMessage(false);
        }
    }, [isHideMessage]);

    const messageText = useMemo(() => {
        let text = message.text;
        text = encodeHtmlChars(text);
        text = replaceEmojiToImage(text);
        text = replaceUrlsToLinks(text);

        return text;
    }, [message.text]);

    const isChildren = useMemo(() =>
        message.children?.filter((message) => message.moderate || messageClient?.id === currentUserId).length
        , [message]);

    if (!message) {
        return null;
    }

    const onOpenMessage = () => {
        if (changeOpenMessageIndex) {
            changeOpenMessageIndex(!isOpenMessage);
        }

        setIsOpenMessage(!isOpenMessage);
    }

    const answerFormVisibleToggle = () => {
        setAnswerFormVisible(!isAnswerFormVisible);
    }
    const handleWriteAnswer = async (data) => {
        try {
            await onWriteAnswer(data);
            setAnswerFormVisible(false);
        } catch (e) { }
    }
    const handleMessageRating = async (value) => {
        if (isOutgoing || ratingUpdating) {
            return;
        }

        //Проверяем авторизацию
        const isAuth = await checkAuth(currentUser);

        if (isAuth) {
            setRatingUpdating(true);

            //Вычисляем итоговое значение, если текущее совпадает с тем, что пришло, значит обнуляем значение
            const _value = userRating === value ? null : value;
            const isOpposite = ['like', 'dislike'].includes(userRating) && ['like', 'dislike'].includes(_value);
            const usersArr = [...(ratedMessages[message.id] || [])];

            //Если в списке сохраненных лайков для этого сообщения есть рейтинг, то обновляем значение, если нет, добавляем юзера
            const targetIdx = usersArr.findIndex(({ userId }) => currentUserId === userId);
            if (targetIdx < 0) {
                usersArr.push({ userId: currentUserId, value: _value })
            } else {
                usersArr[targetIdx].value = _value;
            }
            const storageValue = { ...ratedMessages, [message.id]: usersArr };

            //Обновляем хранилище
            storage.set(ratedStorageKey, storageValue, storage.DAY * 30);

            //Обновляем стейт
            setUserRating(_value);

            try {
                //Если значение уже проставлено, и при этом ставим противоположное, то необходимо сначала удалить старое, затем проставить новое
                if (isOpposite) {
                    await ratingFuncs[type][value === 'like' ? 'dislike' : 'like'](eventKey, message.id, false);
                }
                await ratingFuncs[type][value](eventKey, message.id, value === _value);
            } catch (e) {
                console.error(e);
            } finally {
                setRatingUpdating(false);
            }
        }
    }

    const formatMessageTime = (messageTime) => {
        const currentTime = moment();
        const messageMoment = moment.unix(messageTime);
        const diffInMinutes = currentTime.diff(messageMoment, 'minutes');

        if (diffInMinutes < 60 * 12) {
            return moment.unix(messageTime).format('HH:mm');
        }

        if (diffInMinutes < 60 * 24) {
            return `${Math.floor(diffInMinutes / 60) || 1} ${$locale.get('time.short.hour')}.`;
        }

        return `${Math.floor(currentTime.diff(messageMoment, 'days'))} ${$locale.get('time.short.day')}.`;
    };

    const showMoreDataToggle = () => {
        setIsShowMoreData(status => !status);
    }

    if (!message.moderate && messageClient?.id !== currentUserId) {
        return null;
    }

    const episode = message.episodeId ? episodes?.find(({ id }) => id === message.episodeId) : null;
    const theme = moduleQuestion?.themesEnabled && (chatMessageThemes || []).find(({ id }) => id === message.themeId);

    const classContainer = `${className} ${message.moderate ? "" : className + "-hidden"
        } ${isOutgoing ? "-outgoing" : ""} ${message.parentId ? "-child" : ""}  ${message.children?.length ? "-parent-message" : ""
        } ${avatarsDisabled ? 'avatars-disabled' : ''} ${isLastChild ? '-last-child' : ''}`;
    const classMessageText = `${className}-text ${fixedLine && !isOpenMessage ? `-fixed-line -line-${message.fixed ? 2 : fixedLine}` : ''}`;

    return (
        <div data-id={message.id} className={classContainer}>
            <div className={`${className}-wrapper`}>
                {message.parentId && type === 'question' ? <blockquote className="line-child-message"></blockquote> : null}
                <div>
                    {!avatarsDisabled &&
                        <div className={`${className}-avatart`}>
                            <UserAvatar user={message.anonymous ? null : messageClient} size="medium" />
                            {isChildren ? <blockquote className="line-parent-message"></blockquote> : null}
                        </div>
                    }
                    <div className={`${className}-main`}>
                        <div className={`${className}-header`}>
                            <span className={`${className}-user-nickname`}>
                                {message.anonymous ? $locale.get("messages.anonym") : messageClient?.nickname}
                                {/* {message.anonymous ? $locale.get("messages.anonym") : getUserNickname(messageClient)} */}
                            </span>
                            <span className={`${className}-spacer`} />
                            {(role && !message.anonymous) && <span>{$locale.get(`member.roles.${role}`)}</span>}
                            {!isHideTime && (
                                <div className={`${className}-data`} onClick={showMoreDataToggle} ref={dataRef}>
                                    <span className={`${className}-createdAt`}>{formatMessageTime(message.time)}</span>
                                    <More />
                                </div>
                            )}
                            {isShowMoreData && (
                                <div className={`${className}-more-data`} ref={moreDataRef}>
                                    <p>{moment.unix(message.time).format('DD.MM.YYYY HH:mm')}</p>
                                    {isHybrid && messageClient?.participationType && <p>{$locale.get(`messages.participationType.${messageClient?.participationType}`)}</p>}
                                    {episode && <p>{$locale.getProperty(episode, 'name')}</p>}
                                    {theme && <p>{$locale.getProperty(theme, 'title')}</p>}
                                </div>
                            )}

                            {message.fixed && <PinMessage />}
                        </div>

                        <div className={classMessageText}>
                            {message.targetMember && <span><strong>@{getFullname(message.targetMember, $locale)}</strong>, </span>}
                            <div ref={messageTextRef} dangerouslySetInnerHTML={{ __html: messageText }} />
                        </div>
                        {isCanOpen && (
                            <a className="open-message" onClick={onOpenMessage}>
                                {$locale.get(isOpenMessage ? 'messages.curtail' : 'messages.readMore')}
                            </a>
                        )}
                        <div className={`${className}-footer`}>
                            <div className={`${className}-likes ${className}-likes--rating`}>
                                {likesEnabled && (
                                    <a onClick={() => handleMessageRating('like')} className={`${className}-rating ${userRating === 'like' ? '-active' : ''}`}>
                                        <LikeMessage active={userRating === 'like'} />{!isRatingEnabled && message.likes}
                                    </a>
                                )}
                                {(isRatingEnabled && !message.fixed && (likesEnabled || dislikesEnabled)) && 
                                    <span className={`${className}-rating -active`}>{message.likes - message.dislikes}</span>
                                }
                                {dislikesEnabled && (
                                    <a onClick={() => handleMessageRating('dislike')} className={`${className}-rating -dislike ${userRating === 'dislike' ? '-active' : ''}`}>
                                        <LikeMessage active={userRating === 'dislike'} />{!isRatingEnabled && message.dislikes}
                                    </a>
                                )}
                            </div>
                            <div>
                                {onWriteAnswer && message.moderate && (
                                    <a onClick={answerFormVisibleToggle} className={`${className}-answerVisibleButton`}>{$locale.get("messages.answer")}</a>
                                )}
                                {!message.moderate && (
                                    <div className={`${className}-moderate`}>{$locale.get(`${type}s.moderate`)}</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {message.answered && (
                <div className={`${className}-answered ${!!message.children?.length ? 'bordered' : ''}`}>
                    <span><Checkmark />{$locale.get('messages.answered')}</span>
                </div>
            )}

            {isAnswerFormVisible && onWriteAnswer && (
                <blockquote>
                    <WriteCommentForm
                        onSubmit={handleWriteAnswer}
                        parentMessage={message}
                        mainMessageIdOfThread={message.parentId || message.id}
                        currentUser={currentUser}
                        onClose={() => setAnswerFormVisible(false)}
                        allowAnonymousMessage={allowAnonymousMessage}
                        stream={stream}
                        moduleRef={moduleRef}
                        anonymous={anonymous}
                        setAnonymous={setAnonymous}
                        keyboardShowHandler={keyboardShowHandler}
                    />
                </blockquote>
            )}
        </div>
    );
}

export default Message;
